import type { Section, Task } from '@/src/renderer/lib/db/models'

/**
 * Liegt diese Aufgabe hinter dem **Geschafft-Schalter** — also erledigt und
 * ihre **Liegezeit** ist um?
 */
export function isHiddenCompleted(task: Task, graceIds: Set<string>): boolean {
  return task.status === 'completed' && !graceIds.has(task.id)
}

/**
 * Teilt eine Aufgabenliste in das, was in der Liste steht, und das, was hinter
 * dem **Geschafft-Schalter** liegt.
 *
 * `completed` kommt nach `completedAt` absteigend zurück — zuletzt Erledigtes
 * oben, weil dort fast immer das gesucht wird, was man gerade abgehakt hat.
 * Das ist **Darstellung, keine Ordnung**: `order` und `sectionId` bleiben
 * unberührt (ADR 0019).
 */
export function splitCompleted(
  tasks: Task[] | undefined,
  graceIds: Set<string>,
): { visible: Task[] | undefined; completed: Task[] } {
  if (!tasks) return { visible: tasks, completed: [] }

  const visible: Task[] = []
  const completed: Task[] = []
  for (const task of tasks) {
    if (isHiddenCompleted(task, graceIds)) completed.push(task)
    else visible.push(task)
  }
  if (completed.length === 0) return { visible: tasks, completed }

  completed.sort((a, b) => {
    const aMs = a.completedAt ? new Date(a.completedAt).getTime() : 0
    const bMs = b.completedAt ? new Date(b.completedAt).getTime() : 0
    return bMs - aMs
  })
  return { visible, completed }
}

/** Ein **archivierter Abschnitt** hinter dem Geschafft-Schalter, mit seinen erledigten Aufgaben (#193). */
export interface ArchivedGroup {
  section: Section
  tasks: Task[]
}

/**
 * Ordnet den Haufen hinter dem **Geschafft-Schalter** so, wie er gezeichnet wird (#193): erst das
 * Erledigte außerhalb archivierter Abschnitte flach, dann je archiviertem Abschnitt (nach `order`)
 * seine erledigten Aufgaben. Innerhalb bleibt die Reihenfolge von `completed` (zuletzt Erledigtes
 * oben). `tasks` ist dieselbe Menge wie `completed`, nur umsortiert — die Pfeiltasten der Seite
 * laufen so in derselben Reihenfolge, die man sieht.
 */
export function groupArchivedCompleted(
  completed: Task[],
  archivedSections: Section[],
): { tasks: Task[]; groups: ArchivedGroup[] } {
  if (archivedSections.length === 0) return { tasks: completed, groups: [] }
  const groups = [...archivedSections]
    .sort((a, b) => a.order - b.order)
    .map(section => ({ section, tasks: completed.filter(task => task.sectionId === section.id) }))
  const grouped = new Set(groups.map(g => g.section.id))
  const loose = completed.filter(task => !task.sectionId || !grouped.has(task.sectionId))
  return { tasks: [...loose, ...groups.flatMap(g => g.tasks)], groups }
}
